import NiceModal from "@ebay/nice-modal-react";
import {
    addLoadedStepper,
    StepperType,
    useAllowedChanges,
    useInvestmentStrategyMutation,
    useInvestorProfileQuestionnaireQuestions,
    useInvestorQuestionnaireConfig,
    useQuestionnairePdfMutation,
    useResolveInvestorProfileQuestionnaireMutation,
    useSmsVerificationCodeMutation,
} from "@repo/logic";
import {
    ModalConfirm,
    type PensionQuestionnaireStepExtraProps,
    PensionQuestionnaireStepper,
    type PensionQuestionnaireStepProps,
    type QuestionnaireQueryType,
    PensionQuestionnaireStepperStyled as Styled,
    useStableLatestObject,
} from "@repo/ui";
import { type FC, useCallback, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { usePhoneNumbers, usePrecontractualDocs } from "../../queryHooks/misc";
import { usePensionDetail } from "../../queryHooks/shared";

const PENSION_QUESTIONNAIRE_STEPPER_MODAL_WIDTH = 1000;

export const PensionQuestionnaireStepperWrapper: FC = () => {
    const dispatch = useDispatch();
    const resolveInvestorProfileMutation = useResolveInvestorProfileQuestionnaireMutation();
    const sendSMSVerificationCodeMutation = useSmsVerificationCodeMutation();
    const sendInvestmentStrategyRequestMutation = useInvestmentStrategyMutation();
    const questionsQuery = useInvestorProfileQuestionnaireQuestions({ options: { enabled: false } });
    const questionnaireConfigQuery = useInvestorQuestionnaireConfig({ options: { enabled: false } });
    const precontractualDocsQuery = usePrecontractualDocs({ options: { enabled: false } });
    const phoneNumbersQuery = usePhoneNumbers({ options: { enabled: false } });
    const questionnairePdfMutation = useQuestionnairePdfMutation();

    useEffect(() => {
        dispatch(addLoadedStepper({ stepper: StepperType.PensionQuestionnaire }));
    }, [dispatch]);

    // Stable reference object containing evolving query / mutation instances.
    const queries = useStableLatestObject<QuestionnaireQueryType["queries"]>({
        questionsQuery,
        resolveInvestorProfileMutation,
        questionnaireConfigQuery,
        precontractualDocsQuery,
        phoneNumbersQuery,
        sendSMSVerificationCodeMutation,
        usePensionDetail,
        sendInvestmentStrategyRequestMutation,
        questionnairePdfMutation,
        useAllowedChanges,
    });

    // Memoize modal components mapping to keep stable identity
    const modalComponents = useMemo(
        () => ({
            PaperComponent: Styled.StepperCustomPaper,
            ModalTitleComponent: Styled.StepperInvisibleBox,
            DialogActionsWrapperComponent: Styled.StepperInvisibleBox,
            DialogContentWrapperComponent: Styled.StepperCustomStack,
            DialogComponent: Styled.StepperCustomDialog,
        }),
        [],
    );

    // Memoized factory producing closeAction (captures parentModal per invocation)
    const createCloseAction = useCallback(
        (parentModal?: { hide: () => void; resolve: () => void } | null) => () => {
            parentModal?.resolve();
            parentModal?.hide();
            // Fallback: use imperative hide with modal ID in case parentModal ref is stale
            NiceModal.hide("Nicemodal-PensionQuestionnaireStepperModal");
        },
        [],
    );

    // Memoize content renderer passed to ModalConfirm. Accepts the props shape inferred from ModalConfirm generic
    const renderContent = useCallback(
        ({
            parentModal,
            contractId,
            contractType,
            title,
            name,
        }: PensionQuestionnaireStepExtraProps & {
            parentModal?: { hide: () => void; resolve: () => void } | null;
        }) => {
            const stepperProps: PensionQuestionnaireStepProps & { closeAction?: () => void } = {
                contractId,
                name,
                title,
                closeAction: createCloseAction(parentModal),
                queries,
                contractType,
            };
            return <PensionQuestionnaireStepper {...stepperProps} />;
        },
        [queries, createCloseAction],
    );

    // Memoize close override handler
    const handleCloseOverride = useCallback(
        ({ preventDefault }: { preventDefault: () => void; reason: unknown; modal: unknown }) => {
            // close action is not handled here - it is managed by ChangeSavingsStrategyStepper
            preventDefault();
        },
        [],
    );

    return (
        <ModalConfirm<PensionQuestionnaireStepExtraProps>
            id="Nicemodal-PensionQuestionnaireStepperModal"
            content={renderContent}
            width={PENSION_QUESTIONNAIRE_STEPPER_MODAL_WIDTH}
            title=""
            components={modalComponents}
            onCloseOverride={handleCloseOverride}
        />
    );
};
